import { Box, Typography } from '@mui/material';
import { ReactComponent as Checkmark } from 'assets/checkmark-white.svg';
import { ReactComponent as HandShake } from 'assets/handshake-white.svg';
import { ReactComponent as BulletList } from 'assets/left-to-right-list-bullet.svg';
import { formatDealAmount } from 'helper/short-currency';
import { ReactNode, useEffect, useState } from 'react';
import { DealsService } from 'service/deals.service';
import zeplinColor from 'theme/Color';

type RollupNumberType = 'totalDeals' | 'openDeals' | 'closedDeals';

type DealStats = {
  type: RollupNumberType;
  count: number;
  amount: number;
};

type UIProps = {
  backgroundColor: string;
  iconColor: string;
  icon: ReactNode;
  label: string;
};

type RollupNumberDetailType = {
  [key in RollupNumberType]: Partial<DealStats> & Partial<UIProps>;
};

const StatisticsDetails: RollupNumberDetailType = {
  totalDeals: {
    backgroundColor: zeplinColor.Lavender,
    iconColor: zeplinColor.Blue4,
    icon: <HandShake />,
    label: 'Total Deals',
  },
  openDeals: {
    backgroundColor: zeplinColor.Blue6,
    iconColor: zeplinColor.Blue,
    icon: <BulletList />,
    label: 'Open Deals',
  },
  closedDeals: {
    backgroundColor: zeplinColor.Green5,
    iconColor: zeplinColor.Green3,
    icon: <Checkmark />,
    label: 'Closed Deals',
  },
};

export const DealsStatistics = ({ mine = false }: { mine: boolean }) => {
  const [statisticsData, setStatisticsData] = useState<DealStats[]>([
    { type: 'totalDeals', amount: 0, count: 0 },
    { type: 'openDeals', amount: 0, count: 0 },
    { type: 'closedDeals', amount: 0, count: 0 },
  ]);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const res = await DealsService.getRollupNumbers({ mine });
        setStatisticsData([
          { type: 'totalDeals', amount: res.totalDeals.amount, count: res.totalDeals.count },
          { type: 'openDeals', amount: res.openDeals.amount, count: res.openDeals.count },
          { type: 'closedDeals', amount: res.closedDeals.amount, count: res.closedDeals.count },
        ]);
      } catch (err) {
        console.error('Failed to fetch task statistics:', err);
      } finally {
      }
    };

    fetchStatistics();
  }, [mine]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '8px',
        gap: '24px',
      }}
    >
      {statisticsData.map((item) => (
        <Box
          key={item.type}
          sx={{
            backgroundColor: StatisticsDetails[item.type].backgroundColor,
            width: '100%',
            borderRadius: '16px',
            padding: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Typography variant='responsiveDisplay5'>
              {formatDealAmount(item.amount)} ({item.count})
            </Typography>
            <Typography variant='responsiveBody3'>{StatisticsDetails[item.type].label}</Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              backgroundColor: StatisticsDetails[item.type].iconColor,
              borderRadius: '60px',
            }}
          >
            {StatisticsDetails[item.type].icon}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
